import { useParams } from "react-router-dom";
import { useGetEmojisQuery, useUploadEmojiMutation, useCreateEmojiMutation, useDeleteEmojiMutation } from "@/features/settings/api";
import { Card, Box, Typography, Stack, CircularProgress } from "@mui/joy";
import { InputFileUpload } from "@/components/InputFileUpload";
import { DeleteAction } from "@/components/DeleteAction";
import { useCrudHouseholdMutation } from "@/hooks/useCrudHouseholdMutation";

export function SettingsEmojisPage() {
  const { householdId } = useParams();
  const { data, isLoading } = useGetEmojisQuery({ householdId });
  const [uploadEmoji, { isLoading: isUploading }] = useUploadEmojiMutation();
  const [createEmoji, { isLoading: isCreating }] = useCreateEmojiMutation();
  const useDelete = useCrudHouseholdMutation(householdId, useDeleteEmojiMutation);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !householdId) return;

    try {
      const result = await uploadEmoji({ householdId, file }).unwrap();
      const name = file.name.replace(/\.[^/.]+$/, "");
      await createEmoji({
        householdId,
        data: { name, ...result.data }
      }).unwrap();
    } catch (error) {
      console.error('Failed to upload emoji:', error);
      alert(error.message || 'Failed to upload emoji');
    } finally {
      event.target.value = '';
    }
  };

  const customEmojis = data?.data || [];

  return (
    <Card variant="outlined" sx={{ p: 2.5 }}>
      <Box sx={{ mb: 2 }}>
        <Typography level="h4">Emojis</Typography>
        <Typography level="body-sm">Upload and manage custom emojis for your household</Typography>
      </Box>

      <Stack gap={2}>
        <Box>
          <InputFileUpload
            title={isUploading || isCreating ? "Uploading..." : "Upload Custom Emoji"}
            onChange={handleFileUpload}
          />
        </Box>

        {isLoading ? (
          <CircularProgress />
        ) : customEmojis.length === 0 ? (
          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
            No custom emojis yet. Upload one to get started!
          </Typography>
        ) : (
          <Stack direction="row" gap={1} flexWrap="wrap">
            {customEmojis.map((emoji) => (
              <Box
                key={emoji.id}
                sx={{
                  position: 'relative',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 'sm',
                  p: 1,
                  pt: 2,                  
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  height: 120,
                  width: 120,
                  maxWidth: 120,
                }}
              >
                <img
                  src={emoji.url}
                  alt={emoji.name}
                  style={{
                    width: 64,
                    height: 64,
                    objectFit: 'contain',
                  }}
                />
                <Typography 
                  level="body-xs" 
                  sx={{ 
                    textAlign: 'center',
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {emoji.name}
                </Typography>
                <Box sx={{ position: 'absolute', top: 2, right: 2 }}>
                  <DeleteAction id={emoji.id} name={emoji.name} useDelete={useDelete} />
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

